import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import moment from 'moment'
import { Elements } from '@stripe/react-stripe-js'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'
import Icon from '../../Icons'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import PaymentModal from '../../Payment/PaymentModal/PaymentModal'
import { renderHeaderIconContainer } from '../ChatSidebar/InboxSelector'
import { chatTypeToIconMap } from '../helpers'
import { getMinutesofDayFromDate, getFullDateFromDayMinutes } from '../../../helpers/helpers'
import { daysOfWeek } from '../../../helpers/constants'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import { stripePromise } from './utils'
import PromoPopUp from './PromoPopUp'
import CallingSettings from './components/CallingSettings/CallingSettings'
import AddNewNumber from './AddNewNumber/AddNewNumber'
import CustomReplyWindow from './CustomReply/CustomReplyWindow'

const ADMIN_OR_PM = ['Administrator', 'PropertyManager']

const SelectedInboxDetails = ({
  inbox,
  timezone,
  userRole,
  building,
  updateChatInboxes,
  numberPackageToBuy,
  onClose,
}) => {
  const { t } = useTranslation(['chat'])
  const { displaySnackbar } = useSnackbar()
  const [selectedDaysWithTime, setSelectedDaysWithTime] = useState([])
  const [outOfOfficeMessage, setOutOfOfficeMessage] = useState('')
  const [outOfOfficeMessageActive, setOutOfOfficeMessageActive] = useState(false)
  const [searchReplies, setSearchReplies] = useState('')
  const [customReplyWindowVisibility, setCustomReplyWindowVisibility] = useState(false)
  const [cancelSubWindowVisibility, setCancelSubWindowVisibility] = useState(false)
  const [updateSubWindowVisibility, setUpdateSubWindowVisibility] = useState(false)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [customReplies, setCustomReplies] = useState([])
  const [selectedCustomReply, setSelectedCustomReply] = useState(null)
  const [areaCode, setAreaCode] = useState(null)
  const [isSubmitting, setSubmitting] = useState(false)
  const [isPromoPopUpVisible, setPromoPopUpVisible] = useState(false)

  const selectedDays = selectedDaysWithTime.map((day) => day.name)

  const isAdminOrPM = ADMIN_OR_PM.includes(userRole)
  const isMaintenanceInbox = inbox && inbox.type === 'MT'

  const getDefaultTimingDueToWeekDay = (day) => {
    if (['Su', 'Sa'].includes(day)) {
      return {
        name: day,
        startTime: getFullDateFromDayMinutes(600),
        endTime: getFullDateFromDayMinutes(1020),
      }
    }
    return {
      name: day,
      startTime: getFullDateFromDayMinutes(540),
      endTime: getFullDateFromDayMinutes(1080),
    }
  }

  const handleSelectDay = (day, isActive) => {
    setSelectedDaysWithTime(
      isActive
        ? selectedDaysWithTime.filter((selectedDay) => selectedDay.name !== day)
        : [...selectedDaysWithTime, getDefaultTimingDueToWeekDay(day)],
    )
  }

  const handleChangeTime = (day, fieldName, value) => {
    const updatedSelectedDays = selectedDaysWithTime.map((selected) => {
      if (selected.name === day) {
        return {
          ...selected,
          [fieldName]: value,
        }
      }
      return selected
    })

    setSelectedDaysWithTime(updatedSelectedDays)
  }

  const handleSubmit = () => {
    setSubmitting(true)
    const body = {
      outOfOfficeMessage,
      enableOutOfOfficeMessage: outOfOfficeMessageActive,
      businessHours: selectedDaysWithTime.map((day) => {
        return {
          ...day,
          startTime: getMinutesofDayFromDate(day.startTime),
          endTime: getMinutesofDayFromDate(day.endTime),
        }
      }),
    }
    API.updateChatInboxes(inbox.id, body).then(({ data }) => {
      updateChatInboxes(data)
      setSubmitting(false)
      displaySnackbar(t('chat:selectedInboxDetails.saveSuccess'))
    })
  }

  const handleUpdateCustomReplyList = (replyData) => {
    const updatedReplies = [...customReplies]
    const indexToUpdate = updatedReplies.findIndex((reply) => reply.id === replyData.id)

    if (replyData.toRemove) {
      if (indexToUpdate !== -1) {
        updatedReplies.splice(indexToUpdate, 1)
      }
    } else {
      if (indexToUpdate !== -1) {
        updatedReplies[indexToUpdate] = replyData
      } else {
        updatedReplies.push(replyData)
      }
    }
    updateChatInboxes({ ...inbox, customReplies: updatedReplies })
    setCustomReplies(updatedReplies)
  }

  const handleCloseCustomReplyWindow = () => {
    setCustomReplyWindowVisibility(false)
    setSelectedCustomReply(null)
  }

  const handleCancelSubscription = () => {
    setSubmitting(true)
    try {
      API.cancelNumberSubscription(inbox.id).then(({ data }) => {
        updateChatInboxes(data)
        setCancelSubWindowVisibility(false)
        setSubmitting(false)
        displaySnackbar(t('chat:inboxSettings.subscriptionCanceledSuccess'))
      })
    } catch {
      setCancelSubWindowVisibility(false)
      setSubmitting(false)
      displaySnackbar(t('chat:selectedInboxDetails.cancelSubscriptionError'), true)
    }
  }

  const handleUpdateSubscription = async (body) => {
    setSubmitting(true)
    const updatedBody = {
      ...body,
      areaCode: Number(areaCode),
      creditPackageId: numberPackageToBuy.id,
    }

    try {
      const { data } = await API.updateNumberSubscription(inbox.id, updatedBody)
      if (userRole === 'Administrator') {
        updateChatInboxes(data)
        displaySnackbar(t('chat:inboxSettings.subscriptionUpdatedSuccess'))
      }
      setSubmitting(false)
      return data
    } catch (error) {
      displaySnackbar(t('chat:inboxSettings.subscriptionUpdateError'), true)
      setSubmitting(false)
      throw new Error(error)
    }
  }

  const getFormattedTime = (date, timezone, format) => {
    if (!date) return null
    return formatInTimeZone(date, timezone, format)
  }

  const filteredCustomReplies = customReplies.filter((reply) => {
    if (
      !searchReplies ||
      reply.title.includes(searchReplies) ||
      reply.message.includes(searchReplies)
    ) {
      return true
    }
    return false
  })

  useEffect(() => {
    if (inbox) {
      const newSelectedDays = inbox.businessHours.map((day) => {
        return {
          ...day,
          startTime: getFullDateFromDayMinutes(day.startTime),
          endTime: getFullDateFromDayMinutes(day.endTime),
        }
      })
      setSelectedDaysWithTime(newSelectedDays)
      setOutOfOfficeMessage(inbox.outOfOfficeMessage)
      setOutOfOfficeMessageActive(inbox.enableOutOfOfficeMessage)
      setCustomReplies(inbox.customReplies)
    }
  }, [inbox])

  useEffect(() => {
    if (selectedCustomReply) {
      setCustomReplyWindowVisibility(true)
    }
  }, [selectedCustomReply])

  return (
    <React.Fragment>
      <div className="navigation-container" onClick={onClose}>
        <Icon icon="arrow" />
        <span>{t('chat:selectedInboxDetails.back')}</span>
      </div>
      <div className="inbox__settings-selected-inbox-navbar">
        <div className="left-side">
          <div className="inbox-name-container">
            {renderHeaderIconContainer(chatTypeToIconMap[inbox.type])}
            <div>{inbox.name}</div>
          </div>
        </div>
        <div className="right-side">
          <div className="flex-col">
            <div className="field-name">{t('chat:selectedInboxDetails.phoneNumber')}</div>
            <div className="field-data">{inbox.phone}</div>
          </div>
          <div className="flex-col">
            <div className="field-name">{t('chat:selectedInboxDetails.members')}</div>
            <div className="field-data">{inbox.members.length}</div>
          </div>
          <div className="flex-col">
            <div className="field-name">{t('chat:selectedInboxDetails.createdAt')}</div>
            <div className="field-data">
              {getFormattedTime(inbox.createdAt, timezone, 'd MMM, yyyy - h:mm a')}
            </div>
          </div>
        </div>
      </div>
      <div className="inbox__settings-selected-inbox-body">
        <div className="settings">
          <div className="left-side">
            <div className="section-header">{t('chat:selectedInboxDetails.businessHours')}</div>
            <div className="flex-row">
              <div className="flex-col">
                <div className="standard-header">{t('chat:selectedInboxDetails.businessDays')}</div>
                <div className="desc-header">
                  {t('chat:selectedInboxDetails.selectWorkingDays')}
                </div>
              </div>
              <div className="days-of-week-row">
                {daysOfWeek.map((day) => {
                  const isActive = selectedDays.includes(day)
                  return (
                    <button
                      key={day}
                      className={`button ${isActive ? 'button-primary' : 'button-gray'} day-button`}
                      onClick={() => handleSelectDay(day, isActive)}
                    >
                      {day}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="flex-row" style={{ alignItems: 'flex-start' }}>
              <div className="flex-col">
                <div className="standard-header">
                  {t('chat:selectedInboxDetails.businessHours')}
                </div>
                <div className="desc-header">
                  {t('chat:selectedInboxDetails.selectWorkingHours')}
                </div>
              </div>
              <div className="flex-col">
                {daysOfWeek.map((day) => {
                  return (
                    <div key={day} className="day-timing-row">
                      <div className="day-name">{day}</div>
                      {selectedDays.includes(day) ? (
                        <div className="flex-row">
                          <DateField
                            name="startTime"
                            placeholderText={t('chat:selectedInboxDetails.selectTimePlaceholder')}
                            autoComplete="off"
                            value={
                              selectedDaysWithTime.find((selectedDay) => selectedDay.name === day)
                                .startTime
                            }
                            onChange={(e) => handleChangeTime(day, e.target.name, e.target.value)}
                            minTime={moment().startOf('day').toDate()}
                            maxTime={
                              selectedDaysWithTime.find((selectedDay) => selectedDay.name === day)
                                .endTime
                                ? moment(
                                    selectedDaysWithTime.find(
                                      (selectedDay) => selectedDay.name === day,
                                    ).endTime,
                                  )
                                    .subtract(30, 'minutes')
                                    .toDate()
                                : moment().endOf('day').subtract(30, 'minutes').toDate()
                            }
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={30}
                            timeCaption={t('chat:selectedInboxDetails.endTimeLabel')}
                            dateFormat="hh:mm a"
                            style={{ width: '110px', marginLeft: '20px' }}
                            onChangeRaw={(e) => e.preventDefault()}
                          />
                          <div>{t('chat:selectedInboxDetails.to')}</div>
                          <DateField
                            name="endTime"
                            placeholderText={t('chat:selectedInboxDetails.selectTimePlaceholder')}
                            autoComplete="off"
                            value={
                              selectedDaysWithTime.find((selectedDay) => selectedDay.name === day)
                                .endTime
                            }
                            onChange={(e) => handleChangeTime(day, e.target.name, e.target.value)}
                            minTime={
                              selectedDaysWithTime.find((selectedDay) => selectedDay.name === day)
                                .startTime
                                ? moment(
                                    selectedDaysWithTime.find(
                                      (selectedDay) => selectedDay.name === day,
                                    ).startTime,
                                  )
                                    .add(30, 'minutes')
                                    .toDate()
                                : moment().startOf('day').subtract(30, 'minutes').toDate()
                            }
                            maxTime={moment().endOf('day').toDate()}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={30}
                            timeCaption={t('chat:selectedInboxDetails.endTimeLabel')}
                            dateFormat="hh:mm a"
                            style={{ width: '110px', marginLeft: '20px' }}
                            onChangeRaw={(e) => e.preventDefault()}
                          />
                        </div>
                      ) : (
                        <div className="closed">{t('chat:selectedInboxDetails.closed')}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="flex-row">
              <div className="flex-col">
                <div className="standard-header">
                  <ToggleField
                    name="out-of-office-message-toggle"
                    value={outOfOfficeMessageActive}
                    onChange={() => setOutOfOfficeMessageActive(!outOfOfficeMessageActive)}
                    disabled={isSubmitting}
                  />
                  <div>{t('chat:selectedInboxDetails.outOfOfficeMessage')}</div>
                </div>
                <div className="desc-header">
                  {t('chat:selectedInboxDetails.outOfOfficeMessageDescription')}
                </div>
              </div>
              <div className="message-editor">
                <textarea
                  style={{ outline: 'none' }}
                  type="text"
                  value={outOfOfficeMessage}
                  onChange={(e) => setOutOfOfficeMessage(e.target.value)}
                  maxLength="200"
                  rows="4"
                  placeholder={t('chat:selectedInboxDetails.typeMessage')}
                />
              </div>
            </div>
            <div className="flex-row">
              <button
                style={{ height: 40, width: 140, margin: '0 0 0 auto' }}
                type="submit"
                className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {t('common:actions.save')}
              </button>
            </div>
          </div>
          <div className="right-side">
            <div className="section-header">{t('chat:selectedInboxDetails.inboxPhoneNumber')}</div>
            <div className="flex-row">
              <div className="flex-col" style={{ width: '100%' }}>
                {inbox.phone &&
                inbox.phoneSubscriptionBillingDate &&
                inbox.phoneSubscriptionStatus &&
                inbox.phoneSubscriptionStatus === 'canceled' ? (
                  <div className="subscription-info">
                    <span className="not-active">
                      <Icon icon="exclamationPoint" />{' '}
                      {t('chat:selectedInboxDetails.subscriptionCancelledEndDate', {
                        date: getFormattedTime(
                          inbox.phoneSubscriptionBillingDate,
                          timezone,
                          'MM/dd/yyyy',
                        ),
                      })}
                    </span>
                  </div>
                ) : (
                  <div className="standard-header">{t('chat:selectedInboxDetails.number')}</div>
                )}
                <div className="number-container">{inbox.phone || '-'}</div>
              </div>
              {!inbox.phone ? (
                <button
                  style={{ height: 40, width: 'auto', marginRight: 0, marginLeft: 'auto' }}
                  className="button button-black is-block"
                  onClick={() => setUpdateSubWindowVisibility(true)}
                >
                  {t('chat:selectedInboxDetails.buyANumber')}
                </button>
              ) : inbox.phoneSubscriptionStatus && inbox.phoneSubscriptionStatus !== 'canceled' ? (
                <button
                  style={{ height: 40, width: 'auto', marginRight: 0, marginLeft: 'auto' }}
                  className="button button-gray is-block"
                  onClick={() => setCancelSubWindowVisibility(true)}
                  disabled={!inbox.phone}
                >
                  {t('chat:selectedInboxDetails.cancelSubscription')}
                </button>
              ) : null}
            </div>
            {inbox.phone && isMaintenanceInbox && isAdminOrPM ? (
              <CallingSettings
                building={building}
                inbox={inbox}
                updateChatInboxes={updateChatInboxes}
              />
            ) : null}
            {isMaintenanceInbox && (
              <div className="flex-row call-schedule-wrap">
                <div className="section-header">{t('chat:callingSettings.callSettings')}</div>
                {(building.maintenanceEscalationAvailable && inbox.phone) ||
                userRole === 'Administrator' ? (
                  <Link
                    to="/dashboard/on-call-schedule"
                    state={{
                      inboxId: inbox.id,
                      buildingId: building.id,
                    }}
                  >
                    <button className="call-schedule-btn">
                      {t('chat:selectedInboxDetails.setupOnCallSchedule')}
                    </button>
                  </Link>
                ) : (
                  <button onClick={() => setPromoPopUpVisible(true)} className="call-schedule-btn">
                    {t('chat:selectedInboxDetails.setupOnCallSchedule')}
                  </button>
                )}
              </div>
            )}
            <div className="flex-row">
              <div className="flex-col" style={{ width: '100%' }}>
                <div className="section-header">{t('chat:selectedInboxDetails.customReplies')}</div>
                <div className="custom-replies-search">
                  <SearchField
                    disabled={!customReplies.length}
                    placeholder={t('chat:selectedInboxDetails.searchReplies')}
                    value={searchReplies}
                    onChange={(e) => {
                      setSearchReplies(e.target.value)
                    }}
                  />
                  <button
                    style={{ height: 40, width: 90 }}
                    type="submit"
                    className={`button button-primary is-block ${isSubmitting ? 'is-loading' : ''}`}
                    onClick={() => setCustomReplyWindowVisibility(true)}
                    disabled={isSubmitting}
                  >
                    {t('chat:conversationInfoBar.create')}
                  </button>
                </div>
                {filteredCustomReplies.length ? (
                  filteredCustomReplies.map((reply, idx) => (
                    <div
                      key={idx}
                      className="custom-reply-container"
                      onClick={() => setSelectedCustomReply(reply)}
                    >
                      <div className="flex-col" style={{ width: '100%' }}>
                        <div className="reply-title">{reply.title}</div>
                        <div className="reply-message">{reply.message}</div>
                      </div>
                      <Icon icon="pencil" />
                    </div>
                  ))
                ) : (
                  <div className="no-replies">{t('chat:selectedInboxDetails.noReplies')}</div>
                )}
              </div>
              <div>{null}</div>
            </div>
          </div>
        </div>
      </div>
      {customReplyWindowVisibility && (
        <CustomReplyWindow
          inboxId={inbox.id}
          editCustomReplyData={selectedCustomReply}
          handleCancel={handleCloseCustomReplyWindow}
          updateRepliesList={handleUpdateCustomReplyList}
        />
      )}
      {paymentModalVisible && (
        <Elements stripe={stripePromise}>
          <PaymentModal
            isOpen={paymentModalVisible}
            userRole={userRole}
            buildingId={building.id}
            purchaseItemTitle={t('chat:selectedInboxDetails.phoneNumber')}
            purchaseItemPrice={numberPackageToBuy ? numberPackageToBuy.monthlyPrice : 0}
            handleSubmit={handleUpdateSubscription}
            handleClose={() => setPaymentModalVisible(false)}
          />
        </Elements>
      )}
      <AddNewNumber
        isOpen={updateSubWindowVisibility}
        handleClose={() => setUpdateSubWindowVisibility(false)}
        handleSubmit={(areaCode) => {
          setAreaCode(areaCode)
          setPaymentModalVisible(true)
        }}
      />
      <ConfirmPopup
        title={t('chat:inboxSettings.cancelSubscriptionConfirmTitle')}
        body={t('chat:inboxSettings.cancelSubscriptionBody', {
          endDate: getFormattedTime(inbox.phoneSubscriptionBillingDate, timezone, 'MM/dd/yyyy'),
        })}
        cancelText={t('common:actions.close')}
        confirmText={t('chat:selectedInboxDetails.cancelSubscription')}
        isOpen={cancelSubWindowVisibility}
        cancelCallback={() => setCancelSubWindowVisibility(false)}
        confirmCallback={handleCancelSubscription}
        className="is-warning"
      />
      <PromoPopUp
        buildingId={building.id}
        isVisible={isPromoPopUpVisible}
        onClose={() => setPromoPopUpVisible(false)}
      />
    </React.Fragment>
  )
}

SelectedInboxDetails.propTypes = {
  inbox: PropTypes.object,
  timezone: PropTypes.string,
  userRole: PropTypes.string,
  building: PropTypes.object,
  updateChatInboxes: PropTypes.func,
  numberPackageToBuy: PropTypes.object,
  onClose: PropTypes.func,
}

export default SelectedInboxDetails
